import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Paper, Button, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material'
import { DataGridPremium, useGridApiRef, GridPaginationModel, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium'
import DeleteIcon from '@mui/icons-material/Delete'
import { Project, UserContractor } from 'src/api'
import { DataLoad } from 'src/components'
import { StatefulDialog } from 'src/components/ui'
import { AssignForeman } from 'src/components/forms'
import { DialogNames, SearchParamsKeys } from 'src/enums'
import { useUserContractorListRequest, useRemoveForemanRequest } from 'src/hooks/api'
import { useAddPopupMessage, useDialog, useQueryParams } from 'src/hooks/ui'
import { defaultRowsValuePerPage, defaultPage, defaultPerPage } from 'src/config'
import UserContractorColumns from 'src/components/columns/userContractor'

const baseColumns = [
  UserContractorColumns.Name,
  UserContractorColumns.Email,
  UserContractorColumns.CreatedAt,
  UserContractorColumns.Phone,
  UserContractorColumns.LastActive,
  UserContractorColumns.LastAction
]

interface ProjectUsersProps {
  project: Project
  updateProject: () => void
}

export const ProjectUsers: FC<ProjectUsersProps> = ({ project, updateProject }) => {
  const apiRef = useGridApiRef()
  const addMessage = useAddPopupMessage()
  const [selectedUser, setSelectedUser] = useState<UserContractor | null>(null)
  const dialogName = DialogNames.RemoveForemanFromProject
  const { openDialog, closeDialog } = useDialog(dialogName)
  const [params, setQueryParams] = useQueryParams({
    page: `${defaultPage}`,
    rowsPerPage: `${defaultPerPage}`
  })

  const { tab, ...restParams } = params
  const { data, isInitialLoading, isError, error, refetch } = useUserContractorListRequest({
    params: {
      ...restParams,
      projectId: project.id,
      isActive: true
    }
  })

  const removeRequest = useRemoveForemanRequest({
    options: {
      onSuccess: () => {
        refetch()
        updateProject()
        closeDialog()
        setSelectedUser(null)
      },
      onError: ({ message: text }) => {
        addMessage({ text, type: 'error' })
      }
    }
  })

  const handleRemoveUser = useCallback(
    (row: UserContractor) => () => {
      setSelectedUser(row)
      openDialog()
    },
    [openDialog]
  )

  const handleConfirmRemove = () => {
    if (selectedUser) {
      removeRequest.mutate({
        projectId: project.id,
        userIds: [selectedUser.userId]
      })
    }
  }

  const handleCloseDialog = () => {
    closeDialog()
    setSelectedUser(null)
  }

  const handleAssignForeman = () => {
    refetch()
    updateProject()
  }

  const totalUsers = data?.count || 0

  const columns: GridColDef<UserContractor>[] = useMemo(
    () => [
      ...baseColumns,
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as const,
        width: 50,
        getActions: ({ row }) => [<GridActionsCellItem icon={<DeleteIcon />} label="Remove user" disabled={totalUsers < 2} onClick={handleRemoveUser(row)} showInMenu />]
      }
    ],
    [totalUsers, handleRemoveUser]
  )

  const [rowCountState, setRowCountState] = useState(data?.count || 0)

  useEffect(() => {
    setRowCountState((prev) => (data?.count !== undefined ? data.count : prev))
  }, [data?.count])

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const { page, pageSize } = pagination
    const newPage = page + 1
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${newPage}`,
        [SearchParamsKeys.RowsPerPage]: `${pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  const page = params['page'] ? parseInt(params['page'], 10) - 1 : defaultPage - 1
  const pageSize = params['rowsPerPage'] ? parseInt(params['rowsPerPage'], 10) : defaultPerPage

  return (
    <Grid container item spacing={2} direction="column" wrap="nowrap">
      <Grid item>
        <AssignForeman contractorId={project.contractorId as number} projectId={project.id} updateProject={handleAssignForeman} />
      </Grid>
      <Grid item minWidth={0}>
        <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
          <Paper sx={{ flexGrow: 1, minHeight: '200px', width: '100%' }}>
            <DataGridPremium
              apiRef={apiRef}
              rows={data?.rows || []}
              pagination
              autoHeight
              disableRowSelectionOnClick
              loading={isInitialLoading}
              columns={columns}
              rowCount={rowCountState}
              paginationModel={{
                page,
                pageSize
              }}
              paginationMode="server"
              pageSizeOptions={defaultRowsValuePerPage}
              onPaginationModelChange={handleChangePaginationModel}
              localeText={{
                columnMenuSortAsc: 'Sort A-Z',
                columnMenuSortDesc: 'Sort Z-A'
              }}
              sx={{
                width: '100%',
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  lineHeight: 1,
                  fontSize: '13px',
                  fontWeight: 400,
                  color: 'rgba(0, 0, 0, 0.6)',
                  overflow: 'visible'
                }
              }}
            />
          </Paper>
        </DataLoad>
      </Grid>
      <StatefulDialog
        name={dialogName}
        onClose={handleCloseDialog}
        actions={
          <DialogActions>
            <Button onClick={handleCloseDialog}>No</Button>
            <Button onClick={handleConfirmRemove}>Yes</Button>
          </DialogActions>
        }
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>Do you realy want to remove the user {selectedUser?.name} from the project?</DialogContentText>
        </DialogContent>
      </StatefulDialog>
    </Grid>
  )
}
